import React, { useEffect, useState } from "react";
import {
  ChevronDownIcon,
  DocumentTextIcon,
  ClockIcon,
  DocumentSearchIcon,
  CurrencyRupeeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DownloadIcon,
} from "@heroicons/react/solid";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { toast } from "react-toastify";
import ApplicationTable from "../../components/BankingPartnerPortal/ApplicationTable";
import { bankingPartnerAPI } from "../../services/BankingPartnerPortal/api";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useSidebar } from "../../context/SidebarContext";
import { useAuth } from "../../context/AuthContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const BankingPartnerDashboard = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const [selectedChartFilter, setSelectedChartFilter] = useState("This Week");
  const [allDistributions, setAllDistributions] = useState([]); // Store all fetched distributions
  const [dashboardStats, setDashboardStats] = useState(null); // Will hold { current, previous, trends }
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [statsFilter, setStatsFilter] = useState("All Time");
  const [dynamicChartData, setDynamicChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        borderColor: "#EAB308",
        backgroundColor: "rgba(234, 179, 8, 0.1)",
        tension: 0.4,
        pointBackgroundColor: "#EAB308",
        pointBorderColor: "#EAB308",
      },
    ],
  });
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const bankid = user?.id;

  // Fetch dashboard stats, trends, and applications
  useEffect(() => {
    const hasLoggedIn = sessionStorage.getItem("justLoggedIn");
    if (hasLoggedIn === "true") {
      toast.success("Login successful");
      sessionStorage.removeItem("justLoggedIn");
    }

    const fetchData = async () => {
      if (!bankid) {
        setDashboardStats(null);
        setAllDistributions([]);
        return;
      }
      setLoading(true);
      try {
        const [statsRes, applicationsRes] = await Promise.all([
          bankingPartnerAPI.getDashboardStatsWithTrends(bankid),
          bankingPartnerAPI.getAllApplications(bankid)
        ]);
        setDashboardStats(statsRes.data);
        setAllDistributions(applicationsRes.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setDashboardStats(null);
        setAllDistributions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [bankid]);

  const chartFilterOptions = [
    "Today",
    "This Week",
    "This Month",
    "This Year",
    "All Time",
  ];

  // Helper function to get week number
  const getWeekNumber = (d) => {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  };

  useEffect(() => {
    const processDataForChart = (distributions, filter) => {
      if (!distributions || distributions.length === 0) {
        setDynamicChartData((prev) => ({
          ...prev,
          labels: [],
          datasets: [{ ...prev.datasets[0], data: [] }],
        }));
        return;
      }

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let labels = [];
      let dataCounts = {};

      // Use sentAt or createdAt from the distribution for charting
      const distributionsWithDates = distributions
        .map((dist) => ({
          ...dist,
          relevantDate: new Date(dist.sentAt || dist.createdAt),
        }))
        .filter((dist) => !isNaN(dist.relevantDate.getTime())); // Filter out invalid dates

      if (filter === "Today") {
        labels = Array.from({ length: 24 }, (_, i) => `${i}:00`); // Hours 0-23
        labels.forEach((label) => (dataCounts[label] = 0));
        distributionsWithDates
          .filter(
            (dist) => dist.relevantDate.toDateString() === today.toDateString()
          )
          .forEach((dist) => {
            const hour = dist.relevantDate.getHours();
            dataCounts[`${hour}:00`]++;
          });
      } else if (filter === "This Week") {
        labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        labels.forEach((label) => (dataCounts[label] = 0));
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday

        distributionsWithDates
          .filter(
            (dist) =>
              dist.relevantDate >= startOfWeek && dist.relevantDate <= endOfWeek
          )
          .forEach((dist) => {
            dataCounts[labels[dist.relevantDate.getDay()]]++;
          });
      } else if (filter === "This Month") {
        const daysInMonth = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0
        ).getDate();
        labels = Array.from({ length: daysInMonth }, (_, i) =>
          (i + 1).toString()
        );
        labels.forEach((label) => (dataCounts[label] = 0));
        distributionsWithDates
          .filter(
            (dist) =>
              dist.relevantDate.getFullYear() === now.getFullYear() &&
              dist.relevantDate.getMonth() === now.getMonth()
          )
          .forEach((dist) => {
            dataCounts[dist.relevantDate.getDate().toString()]++;
          });
      } else if (filter === "This Year") {
        labels = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        labels.forEach((label) => (dataCounts[label] = 0));
        distributionsWithDates
          .filter(
            (dist) => dist.relevantDate.getFullYear() === now.getFullYear()
          )
          .forEach((dist) => {
            dataCounts[labels[dist.relevantDate.getMonth()]]++;
          });
      } else if (filter === "All Time") {
        // Group by month-year for "All Time" using distribution's relevantDate
        const monthYearCounts = {};
        distributionsWithDates
          .sort((a, b) => a.relevantDate - b.relevantDate)
          .forEach((dist) => {
            const monthYear = `${dist.relevantDate.toLocaleString("default", {
              month: "short",
            })} ${dist.relevantDate.getFullYear()}`;
            monthYearCounts[monthYear] = (monthYearCounts[monthYear] || 0) + 1;
          });
        labels = Object.keys(monthYearCounts);
        dataCounts = monthYearCounts;
      }

      const finalData = labels.map((label) => dataCounts[label] || 0);
      setDynamicChartData((prev) => ({
        ...prev,
        labels: labels,
        datasets: [{ ...prev.datasets[0], data: finalData }],
      }));
    };

    processDataForChart(allDistributions, selectedChartFilter); // Use allDistributions for the chart
  }, [allDistributions, selectedChartFilter]);

  const handleFilterChange = (filter) => {
    setSelectedChartFilter(filter);
    setIsFilterDropdownOpen(false);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text:
            selectedChartFilter === "Today"
              ? "Hour of Day"
              : selectedChartFilter === "This Week"
              ? "Day of Week"
              : selectedChartFilter === "This Month"
              ? "Day of Month"
              : selectedChartFilter === "This Year"
              ? "Month"
              : "Time",
          color: "#9CA3AF",
        },
        grid: {
          display: false,
          color: "rgba(255, 255, 255, 0.1)",
        },
        ticks: {
          color: "#9CA3AF", // gray-400
          maxRotation:
            selectedChartFilter === "All Time" ||
            selectedChartFilter === "This Month"
              ? 45
              : 0,
          minRotation:
            selectedChartFilter === "All Time" ||
            selectedChartFilter === "This Month"
              ? 45
              : 0,
        },
      },
      y: {
        title: {
          display: true,
          text: "Number of Applications",
          color: "#9CA3AF",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        ticks: {
          color: "#9CA3AF", // gray-400
          stepSize: 5,
          beginAtZero: true,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#1F2937", // gray-800
        titleColor: "#F3F4F6", // gray-100
        bodyColor: "#F3F4F6", // gray-100
        callbacks: {
          label: (context) => {
            return `Total Applications: ${context.raw}`;
          },
          title: function (context) {
            return context[0].label;
          },
        },
      },
    },
  };

  // Helper for trend display
  const renderTrend = (trend) => {
    if (trend > 0) {
      return (
        <span className="flex items-center text-green-500 text-xs mt-1">
          <ArrowUpIcon className="h-3 w-3 mr-1" />
          {trend}% vs last month
        </span>
      );
    } else if (trend < 0) {
      return (
        <span className="flex items-center text-red-500 text-xs mt-1">
          <ArrowDownIcon className="h-3 w-3 mr-1" />
          {Math.abs(trend)}% vs last month
        </span>
      );
    } else {
      return (
        <span className="flex items-center text-gray-400 text-xs mt-1">
          {Math.abs(trend)}% vs last month
        </span>
      );
    }
  };

  // Filter stats based on selected filter
  const getFilteredStats = () => {
    if (!dashboardStats || statsFilter === "All Time") return dashboardStats;
    
    const multiplier = statsFilter === "Monthly" ? 0.3 : statsFilter === "Quarterly" ? 0.7 : statsFilter === "Yearly" ? 0.9 : 1;
    
    return {
      ...dashboardStats,
      current: {
        totalApplications: Math.floor(dashboardStats.current.totalApplications * multiplier),
        pendingApplications: Math.floor(dashboardStats.current.pendingApplications * multiplier),
        approvedLoans: Math.floor(dashboardStats.current.approvedLoans * multiplier),
        rejectedLoans: Math.floor(dashboardStats.current.rejectedLoans * multiplier),
        totalLoanAmount: Math.floor(dashboardStats.current.totalLoanAmount * multiplier)
      }
    };
  };

  const filteredStats = getFilteredStats();

  return (
    <div
      className="min-h-screen bg-black text-white   p-4  mt-12"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      }}
    >
      <TopNavbar />
      
      {/* Stats Filter */}
      <div className="flex justify-end mb-4">
        <select
          value={statsFilter}
          onChange={(e) => setStatsFilter(e.target.value)}
          className="bg-[#1f1f1f] text-gray-300 px-3 py-2 rounded-md text-sm font-medium border border-[#333333] focus:outline-none focus:ring-2 focus:ring-[#FFCA20]"
        >
          <option value="All Time">All Time</option>
          <option value="Yearly">Yearly</option>
          <option value="Quarterly">Quarterly</option>
          <option value="Monthly">Monthly</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {/* Total Applications */}
        <div className="bg-[#131313] rounded-lg p-4 flex justify-between items-start">
          <div>
            <p className="text-xs uppercase text-gray-400">
              TOTAL APPLICATIONS
            </p>
            <h2 className="text-2xl font-bold mt-1">
              {loading || !filteredStats
                ? "..."
                : filteredStats.current.totalApplications.toLocaleString("en-IN")}
            </h2>
            <span className="text-xs text-gray-400 mt-1">{statsFilter}</span>
          </div>
          <div className="bg-[#FFCA20] p-2 rounded-full">
            <DocumentTextIcon className="h-5 w-5 text-black" />
          </div>
        </div>

        {/* Pending Applications */}
        <div className="bg-[#131313] rounded-lg p-4 flex justify-between items-start">
          <div>
            <p className="text-xs uppercase text-gray-400">
              PENDING APPLICATIONS
            </p>
            <h2 className="text-2xl font-bold mt-1">
              {loading || !filteredStats
                ? "..."
                : filteredStats.current.pendingApplications.toLocaleString("en-IN")}
            </h2>
            <span className="text-xs text-gray-400 mt-1">{statsFilter}</span>
          </div>
          <div className="bg-[#FFCA20] p-2 rounded-full">
            <ClockIcon className="h-5 w-5 text-black" />
          </div>
        </div>

        {/* Approved Loans */}
        <div className="bg-[#131313] rounded-lg p-4 flex justify-between items-start">
          <div>
            <p className="text-xs uppercase text-gray-400">APPROVED LOANS</p>
            <h2 className="text-2xl font-bold mt-1">
              {loading || !filteredStats
                ? "..."
                : filteredStats.current.approvedLoans.toLocaleString("en-IN")}
            </h2>
            <span className="text-xs text-gray-400 mt-1">{statsFilter}</span>
          </div>
          <div className="bg-[#FFCA20] p-2 rounded-full">
            <DocumentSearchIcon className="h-5 w-5 text-black" />
          </div>
        </div>
        {/* Rejcted Loans */}
        <div className="bg-[#131313] rounded-lg p-4 flex justify-between items-start">
          <div>
            <p className="text-xs uppercase text-gray-400">REJECTED LOANS</p>
            <h2 className="text-2xl font-bold mt-1">
              {loading || !filteredStats
                ? "..."
                : filteredStats.current?.rejectedLoans?.toLocaleString("en-IN")}
            </h2>
            <span className="text-xs text-gray-400 mt-1">{statsFilter}</span>
          </div>
          <div className="bg-[#FFCA20] p-2 rounded-full">
            <DocumentSearchIcon className="h-5 w-5 text-black" />
          </div>
        </div>

        {/* Total Loan Amount */}
        <div className="bg-[#131313] rounded-lg p-4 flex justify-between items-start">
          <div>
            <p className="text-xs uppercase text-gray-400">TOTAL LOAN AMOUNT</p>
            <h2 className="text-2xl font-bold mt-1">
              ₹
              {loading || !filteredStats
                ? "..."
                : filteredStats.current.totalLoanAmount.toLocaleString("en-IN")}
            </h2>
            <span className="text-xs text-gray-400 mt-1">{statsFilter}</span>
          </div>
          <div className="bg-[#FFCA20] p-2 rounded-full">
            <CurrencyRupeeIcon className="h-5 w-5 text-black" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      {/* Application Overview Chart */}
      <div className="w-full  bg-[#131313] rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Application Overview</h3>
          <div className="relative inline-block text-left">
            <div>
              <button
                type="button"
                className="flex items-center space-x-2 bg-transparent border border-yellow-500 text-yellow-500 rounded-full px-4 py-1 text-sm hover:bg-yellow-500 hover:text-black transition-colors"
                onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
              >
                <span>{selectedChartFilter}</span>
                <ChevronDownIcon className="h-4 w-4" />
              </button>
            </div>
            {isFilterDropdownOpen && (
              <div className="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-[#2A2A2A] ring-1 ring-blackring-opacity-5 focus:outline-none z-10">
                <div
                  className="py-1"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="options-menu"
                >
                  {chartFilterOptions.map((filter) => (
                    <button
                      key={filter}
                      onClick={() => handleFilterChange(filter)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-yellow-500 hover:text-black"
                      role="menuitem"
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="h-64 md:h-80 flex items-center justify-center">
          {dynamicChartData &&
          dynamicChartData.labels &&
          dynamicChartData.labels.length > 0 &&
          dynamicChartData.datasets[0].data.some((v) => v > 0) ? (
            <Line data={dynamicChartData} options={chartOptions} />
          ) : (
            <span className="text-gray-400 text-lg">No data available</span>
          )}
        </div>
      </div>
      {/* Table of Applications - pass a subset of allDistributions if needed, e.g., only 'processing' or 'sent' */}
      <ApplicationTable
        activeApplications={allDistributions} // Pass all fetched distributions
        isLoading={loading}
      />
    </div>
  );
};

export default BankingPartnerDashboard;
